import { type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    LayoutGrid, CalendarDays, UserPlus, ClipboardCheck, Activity, HeartPulse,
    FileText, Camera, IdCard, Boxes, Pill, Wrench, BarChart3, Settings, LogOut,
    PenLine,
} from 'lucide-react';
import PtLogo from '@/components/PtLogo';
import { useAuth } from '@/lib/auth';
import { useTranslation, LanguagePicker } from '@patienttrac/i18n';
import { useOrgContext } from '@/hooks/useOrgContext';

// ============================================================================
// AscShell — persistent Ambulatory Surgery Center operations shell.
// Left navigation for high-frequency ASC roles + a top facility/date bar.
// Nav items map to live routes; not-yet-built surfaces are shown disabled with
// 2026-08-15 "fixing not postponing": every rail item now routes to a live,
// DB-backed surface — no "soon" markers remain.
// ============================================================================

type NavItem = { label: string; icon: ReactNode; to?: string };

const NAV: NavItem[] = [
    { label: 'or.nav.command_center', icon: <LayoutGrid />, to: '/' },
    { label: 'or.nav.schedule', icon: <CalendarDays />, to: '/schedule' },
    { label: 'or.nav.patient_access', icon: <UserPlus />, to: '/patient-access' },
    { label: 'or.nav.preop', icon: <ClipboardCheck />, to: '/pre-op' },
    { label: 'or.nav.intraop', icon: <Activity />, to: '/live-cases' },
    { label: 'or.nav.pacu', icon: <HeartPulse />, to: '/pacu' },
    { label: 'or.nav.documentation', icon: <FileText />, to: '/documentation' },
        { label: 'or.nav.send_consent', icon: <PenLine />, to: '/send-consent' },
    { label: 'or.nav.evidence', icon: <Camera />, to: '/evidence' },
    { label: 'or.nav.preference_cards', icon: <IdCard />, to: '/preference-cards' },
    { label: 'or.nav.inventory', icon: <Boxes />, to: '/supplies' },
    { label: 'or.nav.medications', icon: <Pill />, to: '/medications' },
    { label: 'or.nav.equipment', icon: <Wrench />, to: '/equipment' },
    { label: 'or.nav.reports', icon: <BarChart3 />, to: '/reports' },
    { label: 'or.nav.admin', icon: <Settings />, to: '/admin' },
];

function todayLabel(): string {
    return new Date().toLocaleDateString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
}

export default function AscShell({ children }: { children: ReactNode }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, signOut } = useAuth();
    const { t } = useTranslation();
    const { org_id, isDevMode } = useOrgContext();

    return (
        <div className="min-h-screen flex bg-pt-darker text-pt-text">
            {/* Left navigation */}
            <aside className="w-60 shrink-0 bg-pt-panel border-r border-pt-border flex flex-col">
                <div className="h-14 px-4 flex items-center gap-2.5 border-b border-pt-border">
                    <PtLogo size={26} />
                    <div className="leading-tight">
                        <div className="text-sm font-semibold tracking-wide">
                            PatientTrac <span className="text-pt-gold">ASC</span>
                        </div>
                        <div className="text-[10px] text-pt-text-dim uppercase tracking-wider">
                            Surgery Center Ops
                        </div>
                    </div>
                </div>

                <nav className="flex-1 overflow-y-auto py-2">
                    {NAV.map((item) => {
                        const active = item.to
                            ? item.to === '/'
                                ? location.pathname === '/'
                                : location.pathname.startsWith(item.to)
                            : false;
                        const enabled = !!item.to;
                        return (
                            <button
                                key={item.label}
                                type="button"
                                disabled={!enabled}
                                onClick={() => item.to && navigate(item.to)}
                                className={[
                                    'w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors',
                                    '[&>svg]:w-4 [&>svg]:h-4',
                                    active
                                        ? 'bg-pt-cyan/10 text-pt-text border-l-2 border-pt-cyan'
                                        : enabled
                                            ? 'text-pt-text-muted hover:text-pt-text hover:bg-pt-dark border-l-2 border-transparent'
                                            : 'text-pt-text-dim/60 cursor-not-allowed border-l-2 border-transparent',
                                ].join(' ')}
                            >
                                {item.icon}
                                <span className="flex-1 truncate">{t(item.label)}</span>
                            </button>
                        );
                    })}
                </nav>
            </aside>

            {/* Right column: top bar + content */}
            <div className="flex-1 min-w-0 flex flex-col">
                <header className="h-14 shrink-0 bg-pt-panel border-b border-pt-border px-6 flex items-center justify-between">
                    <div className="flex items-center gap-4 min-w-0">
                        <span className="text-sm font-semibold truncate">
                            {org_id ? t('or.brand.short') : t('or.brand.resolving')}
                        </span>
                        <span className="text-xs text-pt-text-dim font-mono">{todayLabel()}</span>
                        {isDevMode && (
                            <span className="text-[10px] uppercase tracking-wide text-pt-amber border border-pt-amber/30 rounded px-1.5 py-0.5">
                                {t('or.brand.dev_org')}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-xs text-pt-text-muted hidden sm:inline">{user?.email}</span>
                        <button
                            type="button"
                            onClick={signOut}
                            className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-pt-text-muted hover:text-pt-gold transition-colors [&>svg]:w-3.5 [&>svg]:h-3.5"
                        >
                            <LogOut /> {t('or.nav.sign_out')}
                        </button>
                        <LanguagePicker variant="select" className="ml-3 text-xs bg-transparent border border-pt-input-border rounded-md px-2 py-1 text-pt-text-muted" />
                    </div>
                </header>

                <main className="flex-1 min-h-0 overflow-y-auto">{children}</main>
            </div>
        </div>
    );
}
